import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice
} from '@reduxjs/toolkit'
import * as apis from '../../apis'
import { apiType, buildJobData } from '../../apis/helpers'
import isActivated from './is_activated'
import rootFolders, { isRoot } from './root_folders'
import { stubActivities, stubFolders, stubUsers, stubShared } from './stubs'

// Default slice name when no slice name is provided.
const defaultSliceName = 'explorer'

// Global cache of slices as they are created.
const sliceMap = {}

/**
 * Accepts an entity ID or object and returns a map of each ID part or segment.
 * @param {Object|string} entityOrId Entity object or ID
 * @return {{ type: string, subtype: string?, id: number }} Map of ID parts
 */
export function selectItemIdParts (entityOrId) {
  let type, subType, id

  if (typeof entityOrId === 'string') {
    const splitParts = entityOrId.split(':')
    type = splitParts.shift()
    id = splitParts.pop()
    subType = splitParts.shift()
  } else {
    const entity = entityOrId
    type = (entity.root && 'root') ||
      (entity.login && 'user') ||
      (entity.type && 'activity') ||
      (entity.name && 'folder') ||
      'unknown'

    subType = (type === 'root' && entity.key) ||
      (type === 'activity' && apiType(entity)) ||
      undefined

    id = entity.id
  }

  return { type, subType, id: Number(id) }
}

/**
 * Generates the unique item entity ID string for the given entity object.
 * @param {Object} entity
 * @return {string}
 */
export function selectItemId (entity) {
  const partsMap = selectItemIdParts(entity)
  return Object.values(partsMap).filter(part => part).join(':')
}

/**
 * Returns the determined item entity type for the given entity object or ID.
 * @param {Object|string} entityOrId
 * @return {string}
 */
export function selectItemType (entityOrId) {
  return selectItemIdParts(entityOrId).type
}

const itemsAdapter = createEntityAdapter({
  selectId: selectItemId,
  sortComparer (a, b) {
    const collator = new Intl.Collator([], { numeric: true, sensitivity: 'variant' })

    // We use this sort comparer to arrange entities into their "natural" order
    // by which they'll be displayed to the user when sort order is "none".
    const aType = selectItemType(a)
    const bType = selectItemType(b)

    // This groups folders *before* all other entity types. Since folders will
    // only ever be mixed with activities, we don't worry about whether folders
    // should be grouped before other types like "users".
    if (aType === 'folder' && bType !== 'folder') return -1
    if (aType !== 'folder' && bType === 'folder') return 1

    // Arrange folder entities in alphabetical order.
    if (aType === 'folder' && bType === 'folder') {
      return collator.compare(a, b)
    }

    // Arrange activity entities by their position index from the backend.
    if (aType === 'activity' && bType === 'activity') {
      if (a.position < b.position) return -1
      if (a.position > b.position) return 1
    }

    return 0
  }
})

/**
 * Fetches a single account user and returns its entity object.
 * @param {number} userId
 * @return {Object} User entity object or undefined if not found.
 */
async function fetchUser (userId) {
  const usersResponse = await apis.users().read({ 'user_ids[]': userId })
  return usersResponse.length ? usersResponse[0].user : undefined
}

async function loadUser (userId, excludeTypes = []) {
  // First, we fetch this user's entity data b/c we don't always have it stored.
  const userEntity = await fetchUser(userId)

  // Then fetch all items.
  const promises = [
    excludeTypes.includes('folder')
      ? Promise.resolve([])
      : apis.folders().read({ user_id: userEntity.id }),
    excludeTypes.includes('activity')
      ? Promise.resolve([])
      : apis.activities().read({ user_id: userEntity.id, group_id: -1 })
  ]
  let [folders, activities] = await Promise.all(promises)

  // Exclude the default ungrouped folder.
  folders = folders.filter(folder => !isRoot(folder))

  return {
    ancestors: [userEntity],
    items: [...folders, ...activities]
  }
}

/**
 * Returns a consistent state shape for pagination data, given the specified
 * pagination response from the paginated API endpoint.
 * @param {Object} data Pagination meta data from the API endpoint.
 * @return {Object}
 */
function getPageState (paginatedApiData = {}) {
  return paginatedApiData.index
    ? {}
    : {
        index: paginatedApiData.current_page,
        itemLimit: paginatedApiData.per_page,
        itemTotal: paginatedApiData.total_count,
        length: paginatedApiData.total_pages
      }
}

export function getSlice (sliceName = defaultSliceName) {
  if (sliceName in sliceMap) return sliceMap[sliceName]

  const selectors = {}
  const thunks = {}

  const itemSelectors = itemsAdapter
    .getSelectors(state => state[sliceName].items)

  Object.assign(selectors, {
    selectItemIds: itemSelectors.selectIds,
    selectItemEntities: itemSelectors.selectEntities,
    selectAllItems: itemSelectors.selectAll,
    selectTotalItems: itemSelectors.selectTotal,
    selectItemById: itemSelectors.selectById
  })

  selectors.selectDisabledIds = createSelector(
    state => state[sliceName].items.disabled,
    // When "disableAnonymous" is set, we disable all anonymous activities.
    state => state[sliceName].disableAnonymous
      ? Object.entries(selectors.selectItemEntities(state))
        .filter(([_, entity]) => entity.anonymous)
        .map(([id]) => id)
      : [],
    // When "maxSelections" is set, we want to disable all unselected items
    // after the selection count reaches this limit. This will prevent any more
    // items from being selected, thus honoring the restriction.
    state => {
      // Do nothing if no max selections is set.
      if (!state[sliceName].maxSelections) return []

      const allItemIds = selectors.selectItemIds(state)
      const selectedIds = selectors.selectSelectedIds(state)

      // Do nothing if max selections has not yet been reached.
      if (selectedIds.length < state[sliceName].maxSelections) return []

      // Return all unselected IDs to have them added to the disabled list.
      return allItemIds
        .filter(id => !selectedIds.includes(id))
        .map(id => id)
    },
    // When "disableActivityTypes" is set, we want to disable all activities
    // whose type is included in this list.
    state => {
      // Do nothing if disabled activity type list is empty.
      if (!state[sliceName].disableActivityTypes.length) return []

      // Filter out all activity types that are on the disabled list.
      return selectors.selectActivityIds(state)
        .filter(id => {
          const activity = selectors.selectItemById(state, id)
          return state[sliceName].disableActivityTypes.includes(activity.type)
        })
    },
    (disabledIds, disabledAnonIds, disabledByMaxSelections, disabledActivityTypes) => {
      return [
        ...disabledIds,
        ...disabledAnonIds,
        ...disabledByMaxSelections,
        ...disabledActivityTypes
      ]
    }
  )

  selectors.selectPendingIds = createSelector(
    state => state[sliceName].items.pending,
    (ids) => ids
  )

  selectors.selectSelectedIds = createSelector(
    state => state[sliceName].items.selected,
    (ids) => ids
  )

  selectors.selectUpdatedIds = createSelector(
    state => state[sliceName].items.updated,
    (ids) => ids
  )

  /**
   * Returns all activity IDs.
   * @return {Array}
   */
  selectors.selectActivityIds = createSelector(
    selectors.selectItemIds,
    (ids) => ids.filter(id => selectItemType(id) === 'activity')
  )

  /**
   * Returns all activity IDs that are currently selected.
   * @return {Array}
   */
  selectors.selectSelectedActivityIds = createSelector(
    selectors.selectSelectedIds,
    selectors.selectActivityIds,
    (selectedIds, activityIds) => selectedIds
      .filter(id => activityIds.includes(id))
  )

  /**
   * Returns all activities that are currently selected.
   * @return {Array}
   */
  selectors.selectSelectedActivities = createSelector(
    selectors.selectSelectedActivityIds,
    selectors.selectItemEntities,
    (selectedActivityIds, items) => selectedActivityIds
      .map(id => items[id])
  )

  /**
   * Returns all entities that are currently selected.
   * @return {Array}
   */
  selectors.selectSelectedEntities = createSelector(
    selectors.selectSelectedIds,
    selectors.selectItemEntities,
    (selected, items) => selected
      .filter(id => id in items)
      .map(id => items[id])
  )

  selectors.selectCurrentFolder = createSelector(
    state => state[sliceName].ancestors,
    (ancestors) => ancestors[ancestors.length - 1]
  )

  selectors.selectRootFolder = createSelector(
    state => state[sliceName].ancestors,
    (ancestors) => ancestors[0]
  )

  selectors.selectClosestRootFolder = createSelector(
    state => state[sliceName].ancestors,
    (ancestors) => ancestors.filter(ancestor => !!ancestor.root).pop()
  )

  selectors.selectCurrentPage = createSelector(
    state => state[sliceName].page,
    (page) => page.index
  )

  /**
    * Allows you to reposition an item in the list
    * @param {string} itemId Item from the store that is being repositioned.
    * @param {number} newPosition 1-based index of the new position where the
    *   item will reposition to. We use 1-based indices here b/c that is how the
    *   position values are stored in the DB.
    * @return {Promise}
    */
  thunks.repositionItem = createAsyncThunk(
    `${sliceName}/repositionItem`,
    async ({ itemId, newPosition }, thunkAPI) => {
      const item = selectors.selectItemById(thunkAPI.getState(), itemId)
      const itemType = selectItemType(item)

      switch (itemType) {
        case 'activity': {
          // Our backend position API requires we send the adjacent previous and
          // next activities relative to the new position. Thus, we find the
          // 1-based index for the activity previous to the new position.
          const currPosition = item.position
          const indexDiff = newPosition - currPosition
          const prevPosition = indexDiff > 0 ? newPosition : newPosition - 1

          // Get the next/prev entities we're moving between. The previous
          // position found above is 1-based. So we must convert to 0-based
          // index when referencing the entities collection.
          const zeroBasedPrevPosition = prevPosition - 1
          const allActivities = selectors.selectActivityIds(thunkAPI.getState())
            .map(id => selectors.selectItemById(thunkAPI.getState(), id))
          const prevActivity = allActivities[zeroBasedPrevPosition]
          const nextActivity = allActivities[zeroBasedPrevPosition + 1]

          await apis.position(apiType(item), item.id).update({
            next_id: nextActivity?.id,
            next_type: apiType(nextActivity),
            prev_id: prevActivity?.id,
            prev_type: apiType(prevActivity),
            target_id: item.group_id,
            target_type: 'group',
            target_user_id: item.owner_id
          })
          return {
            oldPosition: currPosition
          }
        }

        default:
          throw new Error(`Repositioning "${itemType}" is not supported`)
      }
    }
  )

  async function loadFolder (folderId, excludeTypes = [], thunkAPI) {
    let ancestors, items

    // NOTE: Eventually, we may have a single API that returns both folders and
    // activities. Until then, we have to fetch activities separately. This is
    // true whether we're at the root or not.
    const fetchActivities = excludeTypes.includes('activity')
      ? Promise.resolve([])
      : apis.activities().read({ group_id: folderId })

    if (isRoot(folderId)) {
      const responses = await Promise.all([
        fetchActivities,
        excludeTypes.includes('folder')
          ? Promise.resolve([])
          : apis.folders().read({ include_shared_activity_groups: true })
      ])
      let [activities, folders] = responses

      // Remove the default/ungrouped folder from the list. Eventually, the
      // backend API should take care of this for us.
      folders = folders.filter(folder => !isRoot(folder))

      ancestors = []
      items = [...activities, ...folders]
    } else {
      const responses = await Promise.all([
        fetchActivities,
        apis.folders(folderId).read({ include_shared_activity_groups: true })
      ])
      const [activities, folderInfo] = responses

      // When we're not at the root, we fetch the single folder entity which
      // includes a `children` property that contains all nested folders. It
      // also contains an `ancestors` property that lists all parent folders.
      const folders = excludeTypes.includes('folder') ? [] : folderInfo.children
      ancestors = [...folderInfo.ancestors, folderInfo]
      items = [...activities, ...folders]

      // When our root folder is "account", we need the current folder's owner
      // to be included in the ancestry hierarchy. We may not always have this
      // data.  So we have to fetch it when we don't and manually splice it in.
      const rootFolder = selectors.selectRootFolder(thunkAPI.getState())
      if (rootFolder?.key === 'account') {
        const currUser = thunkAPI.getState().profile.user
        const userFolder = folderInfo.owner_id === currUser?.id
          ? currUser
          : await fetchUser(folderInfo.owner_id)
        ancestors.unshift(userFolder)
      }
    }

    return { ancestors, items }
  }

  /**
  * Generic async action to load the specified item.
  * @param {string} itemId Item entity ID to load
  * @return {Promise}
  */
  thunks.loadItem = createAsyncThunk(
    `${sliceName}/loadItem`,
    async (itemId, thunkAPI) => {
      const { type, subType, id } = selectItemIdParts(itemId)
      const rootFolder = selectors.selectRootFolder(thunkAPI.getState())
      const excludes = [...thunkAPI.getState()[sliceName].excludeTypes]
      const typeCase = [
        type,
        subType || null
      ].filter(part => part).join(':')
      let ancestors, items, meta

      switch (typeCase) {
        case 'root:account': {
          items = (await apis.users().read()).map(entry => entry.user)
          ancestors = [rootFolders().account]
          break
        }

        case 'root:shared': {
          const response = await apis.shared().read({ per_page: 30 })
          items = response.data
          meta = response.meta
          ancestors = [rootFolders().shared]
          break
        }

        case 'root:trash': {
          const response = await apis.trash().read()
          items = response.data
          meta = response.meta
          ancestors = [rootFolders().trash]
          break
        }

        case 'root:activities':
          ({ items } = await loadFolder(id, excludes, thunkAPI))
          ancestors = [rootFolders().activities]
          break

        case 'root:allActivities':
          // Folders in this root view are statically generated and contains
          // only a list of other root folders.
          items = [
            { ...rootFolders().activities, name: 'My activities' },
            rootFolders().shared
          ]
          ancestors = [rootFolders().allActivities]
          break

        case 'folder':
          ({ ancestors, items } = await loadFolder(id, excludes, thunkAPI))
          if (!rootFolder) {
            ancestors = [rootFolders().activities, ...ancestors]
          }
          break

        case 'user': {
          ({ ancestors, items } = await loadUser(id, excludes))
          break
        }

        default:
          throw new Error(`Attempting to load an unhandled type => "${itemId}"`)
      }

      // When root folder is present, we have to manually ensure it is added
      // to the top level of the ancestral hierarchy if it doesn't yet exist.
      if (rootFolder && rootFolder.root && rootFolder.key !== subType) {
        // The "All activities" root folder is special in that it contains other
        // root folders as items. When loading these child root folders, we must
        // also ensure these are persisted in the ancestral hierarchy.
        const subRootFolder = thunkAPI.getState()[sliceName].ancestors[1] || {}
        if (rootFolder.key === 'allActivities' &&
          subRootFolder.root && subRootFolder.key !== subType) {
          ancestors.unshift(subRootFolder)
        }

        // This tacks on the root folder in the first position.
        ancestors.unshift(rootFolder)
      }

      return { ancestors, items, meta }
    })

  thunks.loadMore = createAsyncThunk(
    `${sliceName}/loadMore`,
    async (noArgs, thunkAPI) => {
      const currPage = selectors.selectCurrentPage(thunkAPI.getState())
      const root = selectors.selectClosestRootFolder(thunkAPI.getState())
      let response
      if (root?.key === 'shared') {
        response = await apis.shared().read({
          per_page: 30,
          page: currPage + 1
        })
      } else if (root?.key === 'trash') {
        response = await apis.trash().read({ page: currPage + 1 })
      } else {
        // this should never be hit, but it's here for safety
        window.Rollbar && window.Rollbar.error('Error loading more items', { root })
        throw new Error('Error loading more items', { root })
      }
      return {
        data: response.data,
        meta: response.meta
      }
    }, {
      condition (noArgs, { getState }) {
        // Only continue to load more when no loading state is set and when
        // pagination data is present, implying the currently loaded list
        // connected to a paginated endpoint.
        const { loading, page } = getState()[sliceName]
        const hasNextPage = !!page.index && page.index < page.length
        return !loading && hasNextPage
      }
    }
  )

  thunks.createFolder = createAsyncThunk(
    `${sliceName}/createFolder`,
    async ({ itemIds = [], name }, thunkAPI) => {
      const state = thunkAPI.getState()
      const items = itemIds
        .map(itemId => selectors.selectItemById(state, itemId))
      const data = buildJobData(items)
      data.name = name
      delete data.group_ids

      // Include ID of parent if this folder is being nested in another folder.
      const parent = selectors.selectCurrentFolder(thunkAPI.getState())
      if (parent.id !== rootFolders().activities.id) {
        data.parent_id = parent.id
      }

      return apis.folders().create(data)
    })

  thunks.duplicateItem = createAsyncThunk(
    `${sliceName}/duplicateItem`,
    async (entityId, { getState }) => {
      const item = selectors.selectItemById(getState(), entityId)

      switch (selectItemType(entityId)) {
        case 'activity': {
          const newActivity = await apis.activity(item).duplicate({
            exclude: 'options,results,flash_options,visual_settings'
          })
          return newActivity
        }
        case 'folder': {
          // API returns entities in an array, since it can create multiples.
          // We copied a single item so there's only one item in the response.
          const targetFolder = selectItemIdParts(selectors.selectCurrentFolder(getState()))
          const newFolders = await apis.activities().copy([item], targetFolder.id)
          return newFolders[0]
        }
      }
    }
  )

  thunks.trashItems = createAsyncThunk(
    `${sliceName}/trashItems`,
    async (entityIds, thunkAPI) => {
      const itemsToTrash = entityIds.map(entityId =>
        selectors.selectItemById(thunkAPI.getState(), entityId))
      await apis.activities().trash(itemsToTrash)
    })

  /**
   * Copies a list of items into the target entity, typically a folder.
   * @param {Object} targetEntity Target folder entity as the destination
   * @param {Object} targetUserEntity Target user entity when copying to another user
   * @param {Array.<string>} entityIds List of item IDs from the store that will
   *  be copied into the destination target.
   */
  thunks.copyItems = createAsyncThunk(
    `${sliceName}/copyItems`,
    async ({ targetEntity, targetUserEntity, entityIds }, thunkAPI) => {
      const { dispatch, getState } = thunkAPI
      const items = entityIds.map(itemId =>
        selectors.selectItemById(getState(), itemId))
      const userIds = targetUserEntity ? [targetUserEntity.id] : undefined
      await apis.activities().copy(items, targetEntity.id, userIds)

      // When copying intto the current folder, refresh its content to get the
      // newly copied items from the backend.
      const currentFolderId = selectItemId(
        selectors.selectCurrentFolder(getState()))
      if (currentFolderId === selectItemId(targetEntity)) {
        dispatch(thunks.loadItem(currentFolderId))
      }
    })

  /**
   * Copies a list of items into the target user(s)
   * @param {Array.<string>} entityIds List of item IDs from the store that will
   * @param {Array.<string>} targetUserIds List of user IDs to copy to
   *  be copied into the destination target.
   */
  thunks.sendCopy = createAsyncThunk(
    `${sliceName}/sendCopy`,
    async ({ entityIds, targetUserIds }, thunkAPI) => {
      const { getState } = thunkAPI
      const items = entityIds.map(itemId =>
        selectors.selectItemById(getState(), itemId))
      await apis.activities().copy(items, null, targetUserIds)
    })

  /**
   * Moves a list of items into the specified target entity, typically a folder.
   * @param {Object} targetEntity Target entity as the destination
   * @param {Object} targetUserEntity Target user entity when moving between users
   * @param {Array.<string>} entityIds List of item IDs from the store that will
   *  be moved into the destination target.
   */
  thunks.moveItems = createAsyncThunk(
    `${sliceName}/moveItems`,
    async ({ targetEntity, targetUserEntity, entityIds }, thunkAPI) => {
      const items = entityIds.map(id =>
        selectors.selectItemById(thunkAPI.getState(), id))
      await apis.activities().move(items, targetEntity.id, targetUserEntity?.id)
    }, {
      condition ({ targetEntity }, { getState }) {
        // Cancel the move when attempting to move items into the same folder.
        const currentFolder = selectors.selectCurrentFolder(getState())
        return selectItemId(currentFolder) !== selectItemId(targetEntity)
      }
    }
  )

  /**
   * Moves a list of items into the target user.
   * @param {Array.<string>} entityIds List of item IDs from the store that will
   * @param {Array.<string>} targetUserIds List of user IDs to copy to
   *  be copied into the destination target.
   */
  thunks.changeOwner = createAsyncThunk(
    `${sliceName}/changeOwner`,
    async ({ targetUser, entityIds }, thunkAPI) => {
      const items = entityIds.map(id =>
        selectors.selectItemById(thunkAPI.getState(), id))
      await apis.activities().move(items, null, targetUser.id)

      // refresh the current view to reflect removed item
      const { getState, dispatch } = thunkAPI
      const currentFolderId = selectItemId(
        selectors.selectCurrentFolder(getState()))
      dispatch(thunks.loadItem(currentFolderId))
    })

  thunks.toggleActivatedState = createAsyncThunk(
    `${sliceName}/toggleActivatedState`,
    async ({ entityId, activated }, { getState }) => {
      const activity = selectors.selectItemById(getState(), entityId)
      const rootFolder = selectors.selectRootFolder(getState())

      // When activating items from account activities, always use owner ID of
      // the activity b/c we are activating this on behalf of the subuser.
      // Otherwise, omit it and let the API default it to the logged in user.
      const optionalUserId = rootFolder.key === 'account'
        ? { user_id: activity.owner_id }
        : {}

      if (activated) {
        await apis.activation().update({
          type: activity.type,
          uid: activity.shared_key,
          ...optionalUserId
        })
      } else {
        await apis.activation().delete(optionalUserId)
      }

      return Object.assign({}, activity, {
        activated,
        current: activated
      })
    })

  thunks.toggleLockState = createAsyncThunk(
    `${sliceName}/toggleLockState`,
    async ({ entityIds, lock }, thunkAPI) => {
      const entities = entityIds.map(entityId =>
        selectors.selectItemById(thunkAPI.getState(), entityId))
      lock
        ? await apis.activities().lock(entities)
        : await apis.activities().unlock(entities)
    })

  thunks.renameFolder = createAsyncThunk(
    `${sliceName}/renameFolder`,
    async ({ entityId, newName }, thunkAPI) => {
      const folderToRename =
        selectors.selectItemById(thunkAPI.getState(), entityId)
      return apis.folders(folderToRename.id).update({ name: newName })
    })

  thunks.clearResponses = createAsyncThunk(
    `${sliceName}/clearResponses`,
    async ({ archiveName, entityIds, presenter }, thunkAPI) => {
      const items = entityIds.map(entityId =>
        selectors.selectItemById(thunkAPI.getState(), entityId))
      return apis.activities().clear({
        entities: items,
        name: archiveName,
        presenterId: presenter?.id
      })
    })

  thunks.deleteItems = createAsyncThunk(
    `${sliceName}/deleteItems`,
    async (entityIds, thunkAPI) => {
      const entities = entityIds.map(entityId =>
        selectors.selectItemById(thunkAPI.getState(), entityId))
      await apis.activities().delete(entities)
    })

  thunks.restoreItems = createAsyncThunk(
    `${sliceName}/restoreItems`,
    async (entityIds, thunkAPI) => {
      const entities = entityIds.map(entityId =>
        selectors.selectItemById(thunkAPI.getState(), entityId))
      await apis.activities().move(entities, -1)
    })

  thunks.convertSurveyToFolder = createAsyncThunk(
   `${sliceName}/convertSurveyToFolder`,
   async (entityId, thunkAPI) => {
     const item = selectors.selectItemById(thunkAPI.getState(), entityId)
     return apis.surveys(item.id).convertToFolder()
   }
  )

  thunks.convertFolderToSurvey = createAsyncThunk(
    `${sliceName}/convertFolderToSurvey`,
    async (entityId, thunkAPI) => {
      const item = selectors.selectItemById(thunkAPI.getState(), entityId)
      try {
        return await apis.folders(item.id).convertToSurvey()
      } catch (err) {
        return thunkAPI.rejectWithValue(err.responseJSON)
      }
    }
  )

  thunks.downloadScreenshots = createAsyncThunk(
    `${sliceName}/downloadScreenshots`,
    async (entityIds, thunkAPI) => {
      const entities = entityIds.map(entityId =>
        selectors.selectItemById(thunkAPI.getState(), entityId))
      try {
        await apis.activities().downloadScreenshots(entities)
      } catch (err) {
        return thunkAPI.rejectWithValue(err.responseJSON)
      }
    }
  )

  thunks.downloadSlides = createAsyncThunk(
    `${sliceName}/downloadSlides`,
    async (entityIds, thunkAPI) => {
      const entities = entityIds.map(entityId =>
        selectors.selectItemById(thunkAPI.getState(), entityId))
      try {
        await apis.activities().downloadSlides(entities)
      } catch (err) {
        return thunkAPI.rejectWithValue(err.responseJSON)
      }
    }
  )

  thunks.downloadCSVs = createAsyncThunk(
    `${sliceName}/downloadCSVs`,
    async (entityIds, thunkAPI) => {
      const entities = entityIds.map(entityId =>
        selectors.selectItemById(thunkAPI.getState(), entityId))
      try {
        await apis.activities().downloadCSVs(entities)
      } catch (err) {
        return thunkAPI.rejectWithValue(err.responseJSON)
      }
    }
  )

  thunks.configureItems = createAsyncThunk(
    `${sliceName}/configureItems`,
    async ({ entityIds, options = {} }, thunkAPI) => {
      const entities = entityIds.map(entityId =>
        selectors.selectItemById(thunkAPI.getState(), entityId))
      await apis.activities().multiEdit(entities, options)
    }
  )

  const initialState = {
    actions: false,
    activatable: false,
    ancestors: [],
    breadcrumbs: true,
    disableActivityTypes: [],
    disableAnonymous: false,
    disableTooltips: false,
    dragActions: false,
    excludeTypes: [],
    fields: [],
    generateItemHrefs: false,
    header: false,
    presentationOrder: false,
    inlineFolderCreation: {
      showing: false,
      interacting: false
    },
    items: itemsAdapter.getInitialState({
      disabled: [],
      pending: [],
      selected: [],
      updated: []
    }),
    loading: false,
    maxBreadcrumbs: 4,
    maxSelections: null,
    mode: 'list',
    modeControl: false,
    page: getPageState(),
    selectActions: [],
    selectAsPrimaryAction: false,
    selectable: false,
    selectableTypes: [],
    sortField: 'name',
    sortOrder: 'asc'
  }

  const slice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
      actionsToggled (state, action) {
        state.actions = !!action.payload
      },
      disableAnonymousToggled (state, action) {
        state.disableAnonymous = !!action.payload
      },
      entitiesReplacedWithStubs (state, action) {
        const rootFolder = state.ancestors[0]
        switch (rootFolder.name) {
          case rootFolders().account.name:
            itemsAdapter.setAll(state.items, stubUsers())
            break

          case rootFolders().shared.name:
            itemsAdapter.setAll(state.items, stubShared())
            break

          case rootFolders().activities.name:
          case rootFolders().trash.name:
          default:
            itemsAdapter.setAll(state.items, [
              ...stubActivities(),
              ...stubFolders()
            ])
        }

        // NOTE: This is to see some updated items in the stubs.
        state.items.updated = [state.items.ids[state.items.ids.length - 2]]
      },
      generateItemHrefsToggled (state, action) {
        state.generateItemHrefs = !!action.payload
      },
      itemsDisabled (state, action) {
        state.items.disabled = [...action.payload]
      },
      itemsSelected (state, action) {
        const selectedIds = action.payload
        const allIds = state.items.ids

        // Add new entity IDs to the selected list.
        selectedIds
        // Filter out items already selected or non-existent in the explorer.
          .filter(id => !state.items.selected.includes(id) && allIds.includes(id))
        // Filter out item types that are not selectable.
          .filter(id => !state.selectableTypes.length ||
            state.selectableTypes.includes(selectItemType(id)))
        // Filter out activity types that are disabled.
          .filter(id => {
            if (!state.disableActivityTypes.length) return true

            const type = selectItemType(id)
            const entity = state.items.entities[id]

            return type === 'activity'
              ? !state.disableActivityTypes.includes(entity.type)
              : true
          })
          .forEach(id => state.items.selected.push(id))
      },
      itemsUnselected (state, action) {
        // Subtract unselected ids from currently selected list.
        const unselectedIds = action.payload
        state.items.selected = state.items.selected
          .filter(id => !unselectedIds.includes(id))
      },
      fieldsUpdated (state, action) {
        state.fields = action.payload
      },
      headerToggled (state, action) {
        state.header = !!action.payload
      },
      initActivitiesView (state, action) {
        Object.assign(state, initialState, {
          ancestors: state.ancestors[0]?.key === 'activities'
            ? state.ancestors
            : [rootFolders().activities],
          fields: ['modified', 'count'],
          actions: true,
          activatable: true,
          dragActions: true,
          header: true,
          selectActions: [
            'move',
            'copy',
            'clear',
            'trash',
            'lock',
            'unlock',
            'sendCopy',
            'report',
            'changeOwner',
            'downloadScreenshots',
            'downloadSlides',
            'downloadCSVs',
            'configure',
            'copyVisualSettings',
            'shareResponses'
          ],
          selectable: true,
          sortOrder: 'none'
        }, action.payload)
      },
      initFolderExplorerView (state, action) {
        Object.assign(state, initialState, {
          ancestors: [rootFolders().activities],
          excludeTypes: ['activity'],
          inlineFolderCreation: {
            showing: true,
            interacting: false
          },
          maxBreadcrumbs: 1
        })
      },
      initSharedView (state, action) {
        Object.assign(state, initialState, {
          ancestors: state.ancestors[0]?.key === 'shared'
            ? state.ancestors
            : [rootFolders().shared],
          header: true,
          selectActions: [
            'copy',
            'clear',
            'report'
          ],
          selectable: true
        })
      },
      initAgaveView (state, action) {
        Object.assign(state, initialState, {
          ancestors: state.ancestors[0]?.key === 'activities'
            ? state.ancestors
            : [rootFolders().activities],
          fields: ['modified', 'count'],
          dragActions: true,
          header: true,
          sortOrder: 'none',
          disableActivityTypes: [
            'competition'
          ]
        }, action.payload)
      },
      initAccountView (state, action) {
        Object.assign(state, initialState, {
          ancestors: state.ancestors[0]?.key === 'account'
            ? state.ancestors
            : [rootFolders().account],
          fields: ['count'],
          header: true,
          selectActions: [
            'move',
            'clear',
            'report',
            'copy',
            'lock',
            'unlock'
          ]
        })
      },
      // bulk action explorer on account/admins
      initAccountFolderExplorerView (state, action) {
        Object.assign(state, initialState, {
          ancestors: [rootFolders().account],
          excludeTypes: ['activity'],
          maxBreadcrumbs: 1
        })
      },
      initTrashView (state, action) {
        Object.assign(state, initialState, {
          ancestors: state.ancestors[0]?.key === 'trash'
            ? state.ancestors
            : [rootFolders().trash],
          fields: ['deleted'],
          header: true,
          breadcrumbs: false,
          selectAsPrimaryAction: true,
          selectActions: [
            'restore',
            'delete'
          ],
          selectable: true
        })
      },
      initReportActivitiesView (state, action) {
        const {
          // Allow consumers to start this view with a different root.
          rootItem = rootFolders().activities
        } = action.payload || {}

        Object.assign(state, initialState, {
          ancestors: [rootItem],
          fields: ['count'],
          header: true,
          selectable: true
        })
      },
      // Configures an explorer that lets users select a single activity only.
      initSingleActivitySelector (state, action) {
        Object.assign(state, initialState, {
          ancestors: [rootFolders().activities],
          fields: ['count'],
          header: true,
          maxSelections: 1,
          selectable: true,
          selectableTypes: ['activity']
        })
      },
      // Configures list to disable MQAs and only enable polls.
      onlyPollActivitiesEnabled (state, action) {
        state.disableActivityTypes = [
          'competition',
          'survey'
        ]
      },
      // Configures list to disable all activities except Q&A polls.
      onlyDiscourseActivitiesEnabled (state, action) {
        state.disableActivityTypes = [
          'clickable_image',
          'competition',
          'free_text_poll',
          'multiple_choice_poll',
          'ranking_poll',
          'survey'
        ]
      },
      loadingToggled (state, action) {
        state.loading = !!action.payload
      },
      maxSelectionsUpdated (state, { payload }) {
        // Only allow positive integers for max number of selected items.
        // Anything outside that range resets this to its original value.
        state.maxSelections = payload > 0 ? payload : initialState.maxSelections
      },
      modeUpdated (state, action) {
        state.mode = action.payload
        state.sortOrder = 'none'
      },
      selectAsPrimaryActionToggled (state, action) {
        state.selectAsPrimaryAction = !!action.payload
      },
      selectActionsUpdated (state, action) {
        state.selectActions = action.payload || []
      },
      sortUpdated (state, action) {
        const {
          field = state.sortField,
          order = state.sortOrder
        } = action.payload

        state.sortField = field
        state.sortOrder = order
      },
      inlineFolderCreationInteractionToggled (state, action) {
        state.inlineFolderCreation.interacting = !!action.payload
      },
      dragActionsUpdated (state, action) {
        state.dragActions = action.payload || false
      }
    },
    extraReducers (builder) {
      builder
        .addCase(thunks.loadItem.pending, (state, action) => {
          // When loading the same folder that has existing items, assume this
          // is a silent refresh that does not need the loading flag set.
          const itemId = action.meta.arg
          const currentFolderId = state.ancestors.length && selectItemId(
            state.ancestors[state.ancestors.length - 1])
          const isSilentRefresh = currentFolderId === itemId &&
            state.items.ids.length

          if (!isSilentRefresh) {
            state.loading = true
            state.items.selected = []
          }
          state.page = getPageState()
        })
        .addCase(thunks.loadItem.rejected, (state, action) => {
          state.loading = false
        })
        .addCase(thunks.loadItem.fulfilled, (state, action) => {
          const itemId = action.meta.arg
          const { ancestors, items, meta } = action.payload
          state.loading = false

          // When in shared explorer view, toggle certain column fields on/off
          // depending on whether shared fields are available.
          if (ancestors[0].key === 'shared') {
            const hasSharedFields = Object.values(items)
              .some(entity => 'shared_activity_groups' in entity)
            // Allow activation of shared activities.
            state.activatable = !hasSharedFields
            state.fields = hasSharedFields
              ? ['shared-by', 'shared-with', 'count']
              : ['count']
          }

          // When root folder is "All activities", only enable headers and
          // checkboxes when navigating inside one of its child folders.
          if (ancestors[0].key === 'allActivities') {
            const isBelowRoot = ancestors.length > 1
            state.header = isBelowRoot
            state.selectable = isBelowRoot
          }

          // Modifier for the main explorer view on account activities
          if (sliceName === defaultSliceName && ancestors[0].key === 'account') {
            const viewingPresenterList = !!itemId.includes('account')
            // Account activities presenters are not selectable or activatable.
            state.selectable = !viewingPresenterList
            state.activatable = !viewingPresenterList
            // Presenters don't have a count
            state.fields = viewingPresenterList ? [] : ['count']
          }

          // Update items and ancestors
          itemsAdapter.setAll(state.items, items)
          state.ancestors = ancestors
          state.page = getPageState(meta)
        })

        .addCase(thunks.loadMore.pending, (state, action) => {
          state.loading = true
        })
        .addCase(thunks.loadMore.fulfilled, (state, action) => {
          const { data, meta } = action.payload
          itemsAdapter.addMany(state.items, data)
          state.loading = false
          state.page = getPageState(meta)
        })

        .addCase(thunks.trashItems.pending, (state, action) => {
          const entityIds = action.meta.arg
          state.items.pending = [...entityIds]
        })
        .addCase(thunks.trashItems.fulfilled, (state, action) => {
          const entityIds = action.meta.arg
          itemsAdapter.removeMany(state.items, entityIds)

          state.items.selected = state.items.selected
            .filter(id => !entityIds.includes(id))
        })

        .addCase(thunks.deleteItems.pending, (state, action) => {
          const entityIds = action.meta.arg
          state.items.pending = [...entityIds]
        })
        .addCase(thunks.deleteItems.fulfilled, (state, action) => {
          const entityIds = action.meta.arg
          itemsAdapter.removeMany(state.items, entityIds)
          state.items.selected = state.items.selected
            .filter(id => !entityIds.includes(id))
        })

        .addCase(thunks.restoreItems.pending, (state, action) => {
          const entityIds = action.meta.arg
          state.items.pending = [...entityIds]
        })
        .addCase(thunks.restoreItems.fulfilled, (state, action) => {
          const entityIds = action.meta.arg
          itemsAdapter.removeMany(state.items, entityIds)

          state.items.selected = state.items.selected
            .filter(id => !entityIds.includes(id))
        })

        .addCase(thunks.copyItems.fulfilled, (state, action) => {
          const { entityIds, targetEntity } = action.meta.arg
          const targetId = selectItemId(targetEntity)
          state.items.updated = [targetId]

          // When moving items into a target folder that currently exists in the
          // explorer, manually increment its count.
          const updateEntity = state.items.entities[targetId]
          const updateCount = entityIds
            .filter(id => selectItemType(id) === 'activity').length
          if (updateEntity) {
            itemsAdapter.updateOne(state.items, {
              id: targetId,
              changes: {
                total_items: updateEntity.total_items + updateCount
              }
            })
          }
        })

        .addCase(thunks.moveItems.pending, (state, action) => {
          const { entityIds } = action.meta.arg
          state.items.pending = [...entityIds]
        })
        .addCase(thunks.moveItems.fulfilled, (state, action) => {
          const { entityIds, targetEntity } = action.meta.arg
          const targetId = selectItemId(targetEntity)
          itemsAdapter.removeMany(state.items, entityIds)
          state.items.selected = []
          state.items.updated = [targetId]

          // When moving items into a target folder that currently exists in the
          // explorer, manually increment its count.
          const updateEntity = state.items.entities[targetId]
          const updatedActivityCount = entityIds
            .filter(id => selectItemType(id) === 'activity').length
          if (updateEntity) {
            itemsAdapter.updateOne(state.items, {
              id: targetId,
              changes: {
                total_items: updateEntity.total_items + updatedActivityCount
              }
            })
          }

          // If we moved activities, we need to update the position of the remaining
          // activities, otherwise the 'order' displayed on the left has gaps
          if (updatedActivityCount) {
            const activityIds = state.items.ids.filter(id => selectItemType(id) === 'activity')
            itemsAdapter.updateMany(state.items, activityIds.map((id, index) => ({
              id,
              changes: { position: index + 1 }
            })))
          }
        })

        .addCase(thunks.duplicateItem.fulfilled, (state, action) => {
          const newItem = action.payload
          itemsAdapter.addOne(state.items, newItem)
          state.items.updated = [selectItemId(newItem)]
        })

        .addCase(thunks.renameFolder.pending, (state, action) => {
          const { entityId } = action.meta.arg
          state.items.pending = [entityId]
        })
        .addCase(thunks.renameFolder.fulfilled, (state, action) => {
          const { entityId } = action.meta.arg
          const folder = action.payload
          itemsAdapter.updateOne(state.items, {
            id: entityId,
            changes: { name: folder.name }
          })
          state.items.updated = [entityId]
        })

        .addCase(thunks.clearResponses.pending, (state, action) => {
          const { entityIds } = action.meta.arg
          state.items.pending = [...entityIds]
        })
        .addCase(thunks.clearResponses.fulfilled, (state, action) => {
          const { entityIds } = action.meta.arg
          const updates = entityIds
            .filter(id => selectItemType(id) === 'activity')
            .map(id => ({
              id,
              changes: { results_count: 0 }
            }))

          itemsAdapter.updateMany(state.items, updates)
          state.items.updated = [...entityIds]
        })

        .addCase(thunks.toggleActivatedState.pending, (state, action) => {
          const { entityId } = action.meta.arg
          state.items.pending = [entityId]
        })
        .addCase(thunks.toggleActivatedState.fulfilled, (state, action) => {
          const { entityId } = action.meta.arg
          const activity = action.payload
          const activatedItem = Object.values(state.items.entities)
            .find(isActivated)

          // Clear existing activated item if it is found.
          if (activatedItem) {
            const changes = selectItemType(activatedItem) === 'folder'
              ? { contains_activated_activity: false }
              : { activated: false, current: false }

            itemsAdapter.updateOne(state.items, {
              id: selectItemId(activatedItem),
              changes
            })
          }

          // Now update the entity that was updated.
          itemsAdapter.updateOne(state.items, {
            id: entityId,
            changes: activity
          })
          state.items.updated = [entityId]
        })

        .addCase(thunks.toggleLockState.pending, (state, action) => {
          const { entityIds } = action.meta.arg
          state.items.pending = [...entityIds]
        })
        .addCase(thunks.toggleLockState.fulfilled, (state, action) => {
          const { entityIds, lock } = action.meta.arg

          // Folders do not change visual state when locked or unlocked.
          // So filter them out of explorer entities we need to update.
          const activityIds = entityIds.filter(entityId => selectItemType(entityId) !== 'folder')
          const updates = activityIds.map(id => ({
            id,
            changes: { state: lock ? 'closed' : 'opened' }
          }))
          itemsAdapter.updateMany(state.items, updates)
          state.items.updated = [...entityIds]
        })
        .addCase(thunks.repositionItem.pending, (state, action) => {
          const { itemId } = action.meta.arg
          state.items.pending = [itemId]
        })
        .addCase(thunks.repositionItem.fulfilled, (state, action) => {
          const { itemId, newPosition } = action.meta.arg
          const { oldPosition } = action.payload

          // These are 0-based conversions of the 1-based positions.
          const zeroBasedOldPosition = oldPosition - 1
          const zeroBasedNewPosition = newPosition - 1

          // Use a cloned array to arrange activities into its new order.
          const activityIds = state.items.ids
            .filter(id => selectItemType(id) === 'activity')
          activityIds.splice(zeroBasedOldPosition, 1)
          activityIds.splice(zeroBasedNewPosition, 0, itemId)

          // Now we can simply update each activity with new 1-based positions.
          itemsAdapter.updateMany(state.items, activityIds.map((id, index) => ({
            id,
            changes: { position: index + 1 }
          })))
          state.items.updated = [itemId]
        })

        .addCase(thunks.convertSurveyToFolder.pending, (state, action) => {
          const convertedSurveyEntityId = action.meta.arg
          state.items.pending = [convertedSurveyEntityId]
        })
        .addCase(thunks.convertSurveyToFolder.fulfilled, (state, action) => {
          const convertedSurveyEntityId = action.meta.arg
          const newFolder = action.payload

          itemsAdapter.removeOne(state.items, convertedSurveyEntityId)
          itemsAdapter.addOne(state.items, newFolder)
          state.items.updated = [selectItemId(newFolder)]
        })

        .addCase(thunks.convertFolderToSurvey.pending, (state, action) => {
          const convertedFolderEntityId = action.meta.arg
          state.items.pending = [convertedFolderEntityId]
        })
        .addCase(thunks.convertFolderToSurvey.fulfilled, (state, action) => {
          const convertedFolderEntityId = action.meta.arg
          const newSurvey = action.payload

          itemsAdapter.removeOne(state.items, convertedFolderEntityId)

          // The convert_to_survey API creates the survey at the root, so only add it
          // to the current items if we're in the root folder
          if (isRoot(state.ancestors[state.ancestors.length - 1])) {
            itemsAdapter.addOne(state.items, newSurvey)
            state.items.updated = [selectItemId(newSurvey)]
          }
        })

      // NOTE: Matcher reducers must be defined below all case reducers.
      // Always clear 'updated' state when actions are pending.
        .addMatcher(
          (action) => new RegExp(`${sliceName}/.+/pending`, 'i')
            .test(action.type),
          (state, action) => {
            state.items.updated = []
          }
        )
      // Always clear 'pending' state when actions are done.
        .addMatcher(
          (action) => new RegExp(`${sliceName}/.+/rejected|fulfilled`)
            .test(action.type),
          (state, action) => {
            state.items.pending = []
          }
        )

      // This matcher ensures new entities created by adjacent explorers are
      // kept in sync back into the main explorer.
        .addMatcher(
          (action) => action.type.endsWith('createFolder/fulfilled'),
          (state, action) => {
            const { itemIds = [] } = action.meta.arg
            const newFolder = action.payload
            const newFolderParent =
              newFolder.ancestors[newFolder.ancestors.length - 1] ||
              rootFolders().activities
            const thisParent = state.ancestors[state.ancestors.length - 1] || {}

            // When a new folder is created inside this explorer's current
            // folder, then we need to update its items entities with the new
            // folder along with any items that were moved into it.
            if (newFolderParent.id === thisParent.id) {
              itemsAdapter.addOne(state.items, newFolder)
              state.items.updated = [selectItemId(newFolder)]

              // Remove any items that have been moved into the new folder.
              itemsAdapter.removeMany(state.items, itemIds)
              state.items.selected = state.items.selected
                .filter(id => !itemIds.includes(id))
            }
          }
        )
    }
  })

  sliceMap[slice.name] = {
    name: slice.name,
    reducer: slice.reducer,
    ...slice.actions,
    ...selectors,
    ...thunks
  }
  return sliceMap[slice.name]
}
